import { useState } from 'react'
import { Delete } from 'lucide-react'
import { hapticLight } from '@/lib/haptics'
import { PASSCODE_LENGTH } from '@/lib/passcode'
import { cn } from '@/components/ui/utils'

interface PasscodeEntryProps {
  title: string
  error?: string | null
  // Fired when the last digit lands. The parent decides what happens next and
  // clears the entry by remounting (key={attempt}).
  onComplete: (code: string) => void
  disabled?: boolean
}

const KEYPAD_DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9']

/**
 * Passcode boxes + on-screen keypad. Deliberately renders no <input> so the
 * system keyboard can never appear over the custom keypad.
 */
export function PasscodeEntry({ title, error, onComplete, disabled = false }: PasscodeEntryProps) {
  const [code, setCode] = useState('')

  const pressDigit = (digit: string) => {
    if (disabled || code.length >= PASSCODE_LENGTH) return
    hapticLight()
    const next = code + digit
    setCode(next)
    if (next.length === PASSCODE_LENGTH) onComplete(next)
  }

  const pressBackspace = () => {
    if (disabled || code.length === 0) return
    hapticLight()
    setCode(code.slice(0, -1))
  }

  return (
    <div className="flex w-full flex-col items-center gap-4">
      <p className="text-center text-base font-semibold tracking-[-0.5px] text-content-primary-bold">
        {title}
      </p>
      <div
        className={cn(
          'flex items-center justify-center gap-3',
          error && 'animate-passcode-shake'
        )}
      >
        {Array.from({ length: PASSCODE_LENGTH }, (_, index) => (
          <div
            key={index}
            className="flex size-12 items-center justify-center border border-border-L2 bg-surface-L0 text-lg text-content-primary-bold first:rounded-l-lg last:rounded-r-lg"
          >
            {index < code.length ? '•' : ''}
          </div>
        ))}
      </div>
      {error && (
        <p className="text-center text-sm text-content-negative-bold">{error}</p>
      )}
      <div className="mt-4 grid w-full max-w-[280px] grid-cols-3 gap-x-6 gap-y-2">
        {KEYPAD_DIGITS.map((digit) => (
          <button
            key={digit}
            type="button"
            disabled={disabled}
            onClick={() => pressDigit(digit)}
            className="h-14 rounded-md text-[26px] font-bold text-content-primary-bold active:bg-surface-L1"
          >
            {digit}
          </button>
        ))}
        <div />
        <button
          type="button"
          disabled={disabled}
          onClick={() => pressDigit('0')}
          className="h-14 rounded-md text-[26px] font-bold text-content-primary-bold active:bg-surface-L1"
        >
          0
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={pressBackspace}
          aria-label="Delete last digit"
          className="flex h-14 items-center justify-center rounded-md text-content-primary-bold active:bg-surface-L1"
        >
          <Delete className="size-6" />
        </button>
      </div>
    </div>
  )
}
